"use client";

import { ArrowUpRight, Play } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import Link from "next/link";
import { useRef, useState } from "react";

import type { WordOfWeekTranslations, WordOfWeekVideo } from "./word-of-the-week-types";

type WordOfWeekHeroProps = {
  locale: string;
  selectedVideo: WordOfWeekVideo;
  translations: WordOfWeekTranslations;
};

const formatDate = (timestamp: number) =>
  new Intl.DateTimeFormat("en-CA", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(new Date(timestamp));

const spring = { type: "spring" as const, stiffness: 80, damping: 22 };

export function WordOfWeekHero({
  locale,
  selectedVideo,
  translations,
}: WordOfWeekHeroProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hasStarted, setHasStarted] = useState(false);

  // Reset overlay whenever a different video is selected; adjusting state
  // during render avoids the extra cascading render an effect would cause
  const [prevVideoId, setPrevVideoId] = useState(selectedVideo._id);
  if (prevVideoId !== selectedVideo._id) {
    setPrevVideoId(selectedVideo._id);
    setHasStarted(false);
  }

  const handlePlayClick = () => {
    videoRef.current?.play();
    setHasStarted(true);
  };

  return (
    <section className="grid items-start gap-10 lg:grid-cols-[1.15fr_0.85fr] lg:gap-16 xl:gap-24">
      {/* Left — Editorial typography */}
      <div className="flex flex-col justify-between lg:min-h-[70vh] lg:py-4">
        <div>
          <motion.span
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ ...spring, delay: 0.05 }}
            className="inline-block text-[10px] font-black uppercase tracking-[0.35em] text-brand-red dark:text-brand-gold"
          >
            {translations.eyebrow}
          </motion.span>

          {/* Position-only entrance: opacity-gating the LCP heading hides it
              until hydration and tanks LCP on throttled devices */}
          <motion.h1
            initial={{ y: 20 }}
            animate={{ y: 0 }}
            transition={{ ...spring, delay: 0.12 }}
            className="mt-5 font-display text-[clamp(3rem,8vw,6.5rem)] font-black leading-[0.88] tracking-tighter text-zinc-950 dark:text-white"
          >
            {translations.featuredTitle}
          </motion.h1>

          <motion.p
            initial={{ y: 14 }}
            animate={{ y: 0 }}
            transition={{ ...spring, delay: 0.22 }}
            className="mt-6 max-w-[48ch] text-base leading-relaxed text-zinc-600 dark:text-zinc-400"
          >
            {translations.lead}
          </motion.p>
        </div>

        {/* Meta strip — no containers, just type + space */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-12 flex flex-wrap items-end gap-x-10 gap-y-4 lg:mt-0"
        >
          <div>
            <span className="block text-[9px] font-black uppercase tracking-[0.3em] text-zinc-600">
              {translations.selectedLabel}
            </span>
            <span className="mt-1 block font-display text-2xl font-bold tracking-tight text-zinc-950 dark:text-white lg:text-3xl">
              {formatDate(selectedVideo.uploadedAt)}
            </span>
          </div>

          <Link
            href={`/${locale}/watch`}
            className="group ml-auto inline-flex items-center gap-2 rounded-full bg-zinc-950 px-5 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-white transition-all hover:bg-brand-red active:scale-[0.97] dark:bg-white dark:text-zinc-950 dark:hover:bg-brand-gold"
          >
            {translations.openWatchPage}
            <ArrowUpRight size={14} weight="bold" className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </Link>
        </motion.div>
      </div>

      {/* Right — Video player, minimal frame */}
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ ...spring, delay: 0.18 }}
        className="relative self-start"
      >
        {/* Ambient tinted shadow */}
        <div className="pointer-events-none absolute -inset-6 -z-10 rounded-[3rem] bg-brand-red/[0.04] blur-3xl dark:bg-brand-gold/[0.06]" />

        <div className="overflow-hidden rounded-[1.75rem] bg-zinc-950 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.18)] dark:shadow-[0_40px_80px_-20px_rgba(0,0,0,0.5)]">
          {/* Minimal top bar */}
          <div className="flex items-center gap-3 px-5 py-3">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-red opacity-60" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-brand-red" />
            </span>
            <span className="text-[9px] font-black uppercase tracking-[0.3em] text-white/50">
              {translations.latestBadge}
            </span>
          </div>

          <div className="relative aspect-[9/16] w-full bg-zinc-950">
            <video
              ref={videoRef}
              key={selectedVideo._id}
              controls
              playsInline
              // preload none: even "metadata" pulled 2MB because the MP4's
              // moov atom is at the end of the file; the play overlay fully
              // covers the element so no poster is needed
              preload="none"
              onPlay={() => setHasStarted(true)}
              className="h-full w-full object-cover"
            >
              <source src={selectedVideo.videoUrl} />
            </video>

            {/* Play overlay — hidden once user hits play */}
            {!hasStarted && (
              <button
                type="button"
                onClick={handlePlayClick}
                aria-label={`Play video: ${translations.latestBadge} ${formatDate(selectedVideo.uploadedAt)}`}
                className="absolute inset-0 flex flex-col items-center justify-center gap-6 bg-zinc-950/80 transition-opacity hover:bg-zinc-950/70"
              >
                {/* Brand gradient backdrop */}
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_60%_30%,rgba(196,30,58,0.18)_0%,transparent_70%)]" />

                <div className="relative flex flex-col items-center gap-4">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-red shadow-[0_0_40px_rgba(196,30,58,0.5)] transition-transform hover:scale-110 active:scale-95">
                    <Play size={24} weight="fill" className="ml-1 text-white" />
                  </div>
                  <div className="text-center">
                    <p className="text-xs font-black uppercase tracking-[0.3em] text-white/50">
                      {translations.latestBadge}
                    </p>
                    <p className="mt-1 font-display text-xl font-black tracking-tight text-white">
                      {formatDate(selectedVideo.uploadedAt)}
                    </p>
                  </div>
                </div>
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </section>
  );
}
